// ============================================================================
// "The number" — design reference screen 5. Bryan, 2026-09-18: the page that
// led with three methods and a chart was confusing. So it says the number, the
// safe range, four "why" sentences and the buildings compared against — and the
// method sits behind one disclosure. ⛔ Do not build the methods-first version.
// ============================================================================

import { useMemo, useState } from 'react';
import { Button, ErrorState, Skeleton } from '../../shared/components';
import { useFacilitiesCleaning } from '../account-management/hooks/use-facility-cleaning';
import { fetchSuggestion, priceBid, type BidRow, type Suggestion } from './api';
import { whyLines } from './why';
import { nameWithCleaner } from './comp-names';
import { money, num, pct, perSf, shortDate } from './format';
import { saveText } from './start-form';
import { SaveError } from './fields';
import { CustomerPricePanel, useSavedPrice } from './price-lines';
import { useBidLoad } from './use-bid-load';

interface Comp {
  facility_id: string; facility_name: string; state: string | null; num_or_suites: number | null;
  num_procedure_rooms: number | null; chair_count: number | null; total_sqft: number | null;
  revenue: number | null; revenue_per_sf: number | null; margin: number | null;
  excluded_from_comps: boolean; excluded_reason: string | null;
}

export function PricingPanel({ bid, onNext }: { bid: BidRow; onNext: () => void }) {
  const { data: s, loading, error, refetch } = useBidLoad<Suggestion | null>(() => fetchSuggestion(bid.bid_id), [bid.bid_id, bid.updated_at], null);
  const comps: Comp[] = useMemo(() => (Array.isArray(s?.comps) ? (s!.comps as unknown as Comp[]) : []), [s]);
  const ids = useMemo(() => comps.map((c) => c.facility_id), [comps]);
  const { cleaning } = useFacilitiesCleaning(ids);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const saved = useSavedPrice(bid);

  if (loading) return <Skeleton className="h-64 w-full" />;
  if (error || !s) return <ErrorState body={error ?? 'No suggestion came back.'} onRetry={refetch} />;

  const named = comps.map((c) => ({ ...c, label: nameWithCleaner(c.facility_name, cleaning.get(c.facility_id)) }));
  const why = whyLines(s, named.map((c) => ({ label: c.label, ors: c.num_or_suites, sqft: c.total_sqft, revenue: c.revenue, excluded: c.excluded_from_comps })), bid.market_region);
  const reads = Array.isArray(s.b_reads) ? (s.b_reads as unknown as { label: string; monthly: number; n: number | null }[]) : [];
  const stale = bid.settings_as_of && s.settings_as_of && new Date(s.settings_as_of) > new Date(bid.settings_as_of);

  async function reprice() {
    setBusy(true); setErr(null);
    try { await priceBid(bid.bid_id); } catch (e) { setErr(saveText(e)); } finally { setBusy(false); }
  }

  return (
    <div className="space-y-5">
      <SaveError text={err} />
      <div className="rounded-xl border border-tcs-border bg-tcs-surface p-5">
        <div className="text-xs text-tcs-text-muted">Nightly cleaning{bid.service_freq_per_week ? `, ${bid.service_freq_per_week} nights` : ''}</div>
        <div className="mt-1 text-4xl font-semibold tabular-nums text-tcs-text">{money(s.recommended_monthly)}</div>
        <div className="mt-1 text-sm text-tcs-text-secondary">
          per month{s.incumbent_monthly_rate != null && s.premium_recommended_pct != null ? ` · ${Number(s.premium_recommended_pct) >= 0 ? `${pct(s.premium_recommended_pct)} above` : `${pct(-Number(s.premium_recommended_pct))} below`} what they pay today (${money(s.incumbent_monthly_rate)})` : ''}
          {s.sqft_estimated ? ' · square footage estimated from the chairs' : ''}
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2 text-center text-sm">
          <div className="rounded border border-st-danger-border bg-st-danger-bg p-2"><div className="text-xs text-st-danger-fg">Don&apos;t go below</div><div className="font-semibold tabular-nums">{money(s.floor_monthly)}</div></div>
          <div className="rounded border border-st-ok-border bg-st-ok-bg p-2"><div className="text-xs text-st-ok-fg">Recommended</div><div className="font-semibold tabular-nums">{money(s.recommended_monthly)}</div></div>
          <div className="rounded border border-st-warn-border bg-st-warn-bg p-2"><div className="text-xs text-st-warn-fg">Top of range</div><div className="font-semibold tabular-nums">{money(s.top_monthly)}</div></div>
        </div>
      </div>

      {/* the monthly price with what step 3 adds to the nightly line (Ben, 2026-10-15, #841) */}
      {saved.data && <div className="max-w-md"><CustomerPricePanel price={saved.data.price} view={saved.data.view} /></div>}

      <ul className="space-y-3">
        {why.map((w) => (
          <li key={w.key} className="text-sm leading-relaxed text-tcs-text"><span className="font-semibold">{w.title}. </span>{w.text}</li>
        ))}
      </ul>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-tcs-text">The buildings we compared against</h3>
        {named.length === 0 ? (
          <p className="text-sm text-tcs-text-muted">No {bid.facility_type.replace(/_/g, ' ').toLowerCase()} buildings in the book yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-tcs-text-muted">
                <tr className="border-b border-tcs-border">
                  <th className="px-2 py-2">Account</th>
                  <th className="px-2 py-2">{bid.facility_type === 'Dialysis' ? 'Chairs' : 'ORs'}</th>
                  <th className="px-2 py-2 text-right">Sqft</th><th className="px-2 py-2 text-right">Monthly</th>
                  <th className="px-2 py-2 text-right">$/sf</th><th className="px-2 py-2 text-right">Margin</th>
                </tr>
              </thead>
              <tbody>
                {named.map((c) => (
                  <tr key={c.facility_id} className={`border-b border-tcs-border last:border-0 ${c.excluded_from_comps ? 'text-tcs-text-muted' : ''}`}>
                    <td className="px-2 py-2">
                      {c.label}{c.state ? ` · ${c.state}` : ''}
                      {c.excluded_from_comps && <div className="text-xs">Left out of the comparables — {c.excluded_reason ?? 'excluded on the settings screen'}</div>}
                    </td>
                    <td className="px-2 py-2">{bid.facility_type === 'Dialysis' ? num(c.chair_count) : `${num(c.num_or_suites)}${c.num_procedure_rooms ? ` + ${c.num_procedure_rooms} PR` : ''}`}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{num(c.total_sqft)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{money(c.revenue)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{perSf(c.revenue_per_sf)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{pct(c.margin)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <details className="rounded-lg border border-tcs-border p-3 text-sm">
        <summary className="cursor-pointer font-medium text-tcs-text">How the app got here</summary>
        <div className="mt-3 space-y-2 text-tcs-text-secondary">
          {bid.facility_type === 'Dialysis' ? (
            <p><b>A · the rate card.</b> {num(s.total_sqft)} sf × {perSf(s.dialysis_rate_per_sf)}/sf for {bid.service_freq_per_week ?? '—'} nights{s.small_site_floor != null ? `, never less than the small-site floor of ${money(s.small_site_floor)}` : ''}. The sub is {pct(s.sub_cost_share)} of that: {money(s.sub_estimate)}.</p>
          ) : (
            <p><b>A · sub cost by zone ({s.rates_from ?? '—'}).</b> {num(s.sterile_sqft)} sterile sf × {perSf(s.sub_rate_sterile)}{Number(s.or_factor) < 1 ? ` × ${s.or_factor} (ORs cleaned fewer nights)` : ''} + {num(s.non_sterile_sqft)} non-sterile sf × {perSf(s.sub_rate_non_sterile)}{Number(s.sub_intercept) > 0 ? ` + ${money(s.sub_intercept)} fixed` : ''} = {money(s.sub_estimate)} a month. At {pct(s.target_margin)} that is {money(s.recommended_monthly)}; at the {pct(s.floor_margin)} floor, {money(s.floor_monthly)}.</p>
          )}
          <p><b>B · what the book bills.</b> {reads.length ? reads.map((r) => `${r.label}${r.n ? ` (${r.n})` : ''} → ${money(r.monthly)}`).join(' · ') : 'no reads yet'}. Top of range = the highest read, capped at 15% over recommended{s.b_note ? ` (${s.b_note})` : ''}.</p>
          {bid.facility_type !== 'Dialysis' && (
            <p><b>C · the labor check.</b> {num(s.c_hours_per_night)} crew-hours a night × {num(s.c_nights_per_month)} nights × {money(s.c_hourly_rate)}/hr × {s.c_overhead ?? '—'} overhead = {money(s.c_sub)} sub → {money(s.c_price)} at target.</p>
          )}
          <p>Priced with the settings as of {shortDate(s.settings_as_of)}. Rounded to {money(s.price_rounding)}.</p>
        </div>
      </details>

      <div className="flex flex-wrap items-center justify-end gap-3">
        {stale && <span className="text-xs text-st-warn-fg">A rate has changed since this bid was priced.</span>}
        <Button variant="secondary" onClick={reprice} loading={busy} disabled={bid.status === 'Approved'}>Price it again with today&apos;s rates</Button>
        <Button onClick={onNext}>Review with an executive</Button>
      </div>
    </div>
  );
}
